import styles from "./styles.module.scss";
import SecondaryPageHeader from "components/general/SecondaryPageHeader";
import Button from "components/general/Button";
import DebouncedSearch from "components/inputs/DebouncedSearch";
import PermissionsList from "components/cards/PermissionsList";
import Text from "components/general/Text";
import { useEffect, useState } from "react";
import RoleCard from "components/cards/RoleCard";
import EmptyState from "components/general/EmptyState";
import AddRoleDrawer from "components/modals/Permissions/AddRole";
import EditUsers from "components/modals/Permissions/EditUsers";
import Icon from "components/general/Icon";
import { Divider, Dropdown, Spin } from "antd";
import COLORS from "constants/Colors";
import {
  useDeleteRoleMutation,
  useGetAllPreviewUsersQuery,
  useGetRolesQuery,
} from "apis/services/permissions";
import showSuccessMsg from "utils/showSuccessMsg";
import HandleErrors from "utils/handleErrors";
import { PermissionType } from "components/cards/RoleCard/types";
import usePermissions from "hooks/usePermission";


export default function Roles() {
  const [search, setSearch] = useState("");
  const { data: roles, isLoading } = useGetRolesQuery({
    page_size: 30,
    ordering: "-id",
    search,
  });
  const { hasPermissionTo, isSuperUser } = usePermissions()
  const [selectedRole, setSelectedRole] = useState(roles?.results?.[0] || {});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditUsersModalOpen, setIsEditUsersModalOpen] = useState(false);
  const [deleteRole, { isLoading: isDeletingRole }] = useDeleteRoleMutation();
  // All pages are needed: these ids pre-select super users in Edit Users, and saving sends the full list
  const { data: users } = useGetAllPreviewUsersQuery({ is_superuser: true }, { skip: !isSuperUser });

  const addRolesPermission = hasPermissionTo("add_roles_permission_template")
  const editRolesPermission = hasPermissionTo("assign_roles_permission")

  const superUser: PermissionType = {
    id: "super_user",
    name: "Super Users",
    permissions: [],
    staff_ids: users?.map((user: any) => user?.id) || [],
    avatars: [],
    members: users?.length || 0,
    lastUpdated: "",
    description: "Users with this role have all permissions and cannot be edited or deleted.",
    is_default: false,
    is_superuser: true,
  };


  useEffect(() => {
    if (roles?.results?.length) {
      setSelectedRole(roles.results[0]);
    }
  }, [roles]);

  const onCardClick = (role: any) => {
    setSelectedRole(role);
  };

  const handleDeleteRole = () => {
    if (selectedRole?.is_default) return;
    deleteRole(selectedRole.id)
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: "Role deleted successfully" });
      })
      .catch(HandleErrors);
  };

  return (
    <section>
      <AddRoleDrawer isVisible={isModalOpen} setIsVisible={setIsModalOpen} />
      <EditUsers
        isVisible={isEditUsersModalOpen}
        setIsVisible={setIsEditUsersModalOpen}
        selectedStaffIds={selectedRole.staff_ids}
        roleName={selectedRole.name}
        roleId={selectedRole.id}
        isDefault={selectedRole.is_default}
        isSuperUserRole={selectedRole.is_superuser}
      />
      <SecondaryPageHeader
        title="Roles & Permissions"
        RightComponent={
          <div className={styles.headerRight}>
            <DebouncedSearch
              defaultValue={search}
              placeholder="Search users or roles"
              onSearchChange={(value) => setSearch(value)}
              time={500}
            />
            {addRolesPermission &&
              <Button
                btnClassName={styles.headerBtn}
                onClick={() => setIsModalOpen(true)}
              >
                + Create New Role
              </Button>
            }
          </div>
        }
      />
      {isLoading ? (
        <div className="d-flex my-5">
          <Spin className="m-auto" />
        </div>
      ) : roles?.results?.length === 0 ? (
        <EmptyState
          title={search ? "No roles found" : "No roles created yet"}
          description={
            search
              ? "No roles found matching your search"
              : "Roles define what actions users can perform in the system. Create a role to manage permissions and assign it to users."
          }
          buttonText={search ? "Clear Search" : "+ Create New Role"}
          onClick={() => (search ? setSearch(" ") : setIsModalOpen(true))}
        />
      ) : (
        <div className={styles.container}>
          {/* Roles List */}
          <div className={`${styles.paddingBlock32} col-5`}>
            <div className={styles.rolesHeader}>
              <Text className={styles.title}>All Roles</Text>
              <Text
                className={styles.description}
              >{`${roles?.results?.length || 0} roles configured`}</Text>
            </div>
            <div className={styles.rolesList}>
              {roles?.results?.map((role: any) => (
                <RoleCard
                  key={role.id}
                  data={role}
                  isSelected={selectedRole.id === role.id}
                  onClick={() => onCardClick(role)}
                />
              ))}
              {isSuperUser && <>
                <Divider style={{ margin: "16px 0", borderColor: "#E5E7EB" }} />
                <RoleCard
                  key={superUser.id}
                  data={superUser}
                  isSelected={selectedRole.id === superUser.id}
                  onClick={() => onCardClick(superUser)}
                  membersCount={superUser.members}
                />
              </>}
            </div>
          </div>
          {/* Seperator */}
          <div
            style={{
              alignSelf: "stretch",
              width: 1,
              backgroundColor: "#E5E7EB",
            }}
          />

          {/* Permissions List */}
          {selectedRole?.id && (
            <div
              className={`${styles.paddingBlock32}`}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 36,
              }}
            >
              <div className="d-flex justify-between w-100 gap-3">
                <RoleCard
                  data={selectedRole}
                  containerStyle={styles.selectedRoleCard}
                />
                <div>
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "1",
                          label: "Edit Users in this role",
                          disabled: !editRolesPermission,
                          dashed: !editRolesPermission,
                          onClick: () => setIsEditUsersModalOpen(true),
                        },
                        {
                          key: "2",
                          label: "Delete Role",
                          disabled: selectedRole?.is_default,
                          style: {
                            color: selectedRole?.is_default
                              ? "#9ca3af"
                              : COLORS.primary,
                          },
                          onClick: handleDeleteRole,
                        },
                      ],
                    }}
                  >
                    <div style={{ cursor: "pointer" }}>
                      <Icon name="Menu" size={24} color="dark" />
                    </div>
                  </Dropdown>
                </div>
              </div>
              <PermissionsList
                id={selectedRole?.id}
                name={selectedRole?.name}
                isDefault={selectedRole?.is_default}
                isSuperUser={selectedRole?.is_superuser}
                permissions={selectedRole?.permissions}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
